import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { getUnixTimestamp, getUUID } from 'helpers/utils';
import { errorToast, successToast } from 'configuration/notifications';
import { cashWarrantApi } from 'helpers/api/cashWarrant';

import { CashWarrant } from 'store/cashWarrant/actions';
import { RentContinue, Rent } from 'store/transferAct/actions';
import { useFirstRender } from 'hooks/useFirstRender';

import { rentContinueSelector, rentSelector } from 'store/transferAct/selectors';
import { cashBoxListSelector, cashWarrantStatus } from 'store/cashWarrant/selectors';
import { userDataSelector } from 'store/auth/selectors';

import { ButtonIcon } from 'components/Button';
import { ModalAttention } from 'components/ModalAttention';
import { Header } from 'components/Header';

import { CashWarrantForm } from 'components/CashWarrantForm';
import { CashWarrantCard } from 'components/CashWarrantCard';

import './style.scss';
import { BaseService, CashWarrantService } from 'services';

const CheckoutRentContinuePage = () => {
  const { rentId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const rent = useSelector(rentContinueSelector);
  const rentMain = useSelector(rentSelector);
  const cashBoxes = useSelector(cashBoxListSelector);
  const userData = useSelector(userDataSelector);
  const warrantStatus = useSelector(cashWarrantStatus);

  const [copiedCashWarrantID, setCopiedCashWarrantID] = useState(null);

  const [attentionModal, setAttentionModal] = useState(null);
  const [pending, setPending] = useState(false);
  const [isPledge, setIsPledge] = useState(false);
  const [cashWarrants, setCashWarrants] = useState([]);
  const [editingCashWarrantId, setEditingCashWarrantId] = useState(undefined);
  const firstRender = useFirstRender();

  useEffect(() => {
    if (firstRender) {
      dispatch(CashWarrant.getCashBoxes());
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      if (cashBoxes) {
        if (rentId) {
          getCashWarrants(rentId);
          return;
        }
        createCashWarrant();
      }
    };
    init();
  }, [cashBoxes]);

  useEffect(() => {
    if (cashWarrants.length === 1) {
      setCashWarrants([{ ...cashWarrants[0], sum: rentSum }]);
    }
  }, [rentSum]);

  useEffect(() => {
    if (warrantStatus !== 0) {
      dispatch(CashWarrant.setCashWarrantStatus(0));
      history.push(copiedCashWarrantID ? `/rent/${rent?.actID1C}/checkout` : '/');
      const init = async () => {
        if (copiedCashWarrantID) {
          const { data } = await cashWarrantApi.getCashWarrantUrl(copiedCashWarrantID);
          const url = data.url;
          BaseService.copyUrl(url);
          dispatch(Rent.getRent(rent.actID1C));
          setCopiedCashWarrantID(null);
        }
      };
      init();
    }
  }, [warrantStatus, dispatch, history, copiedCashWarrantID, rent]);

  const handleUrlClick = async (currentWarrant) => {
    let url = currentWarrant.url;
    if (!url) {
      setCopiedCashWarrantID(currentWarrant.warrantID1C);
      await handleSave();
      return;
    }
    BaseService.copyUrl(url);
  };

  const itemsSurchargeAmountSum = useCallback(() => {
    if (!rent?.instruments) {
      return 0;
    }
    return rent.instruments.reduce((acc, cur) => acc + cur.AdditionalRentTerm_amount * cur.quantity, 0);
  }, [rent]);

  const getAmountSum = () => {
    const surchargeAmountSum = itemsSurchargeAmountSum();
    return Math.abs(surchargeAmountSum);
  };

  const rentSum = useMemo(() => {
    let sum = 0;

    sum += rent.fine.reduce((acc, n) => acc + Number(n.sum), 0);
    sum -= rent.discount.reduce((acc, n) => acc + Number(n.sum), 0);

    return sum + getAmountSum();
  }, [rent, isPledge]);

  const getCashWarrants = (id) => {
    cashWarrantApi.getCashWarrants(id).then(({ data } = {}) => {
      const newCashWarrants = CashWarrantService.mapWarrants(data.results);
      if (newCashWarrants.length > 0) {
        setCashWarrants(newCashWarrants);
        setIsPledge(data.results[0].isPledge);
        createCashWarrant(newCashWarrants);
        return;
      }
      createCashWarrant();
    });
  };

  const handleClose = (isSubmit) => {
    if (!isSubmit) {
      setAttentionModal(true);
      return;
    }
    dispatch(RentContinue.clearRent());

    toggleAttentionModal(false);
    history.push('/');
  };

  const toggleAttentionModal = (toggle) => {
    setAttentionModal(toggle);
  };

  const handleBack = async () => {
    const backUrl = rent?.actID1C ? `/rent/${rent?.actID1C}/continue` : '/rent';
    history.push(backUrl, { fromCheckout: true });
  };

  const handleSave = async () => {
    const cashWarrantsSum = cashWarrants.filter((elem) => elem.createdNow).reduce((acc, cur) => acc + cur.sum, 0);

    if (cashWarrants.length === 0) {
      return errorToast('Нужно добавить хотя бы один кассовый ордер!');
    }

    if (cashWarrants.every((w) => !w.nonConduct) && rentSum !== cashWarrantsSum) {
      return errorToast(`Сумма в кассовых ордерах должна быть равна сумме в аренде (${rentSum} != ${cashWarrantsSum})`);
    }

    for (const warrant of cashWarrants) {
      if (warrant.sum <= 0) {
        return errorToast('Сумма в кассовых ордерах должна быть больше нуля');
      }

      if (warrant.comment.length === 0 && isPledge) {
        return errorToast('В случае принятия залога необходимо указывать комментарий!');
      }

      if (warrant.comment.length === 0 && warrant.nonConduct) {
        return errorToast('Комментарий обязателен для неофициального приходника!');
      }

      if (!warrant.selected) {
        return errorToast('Нужно выбрать кассу!');
      }
    }

    if (pending) {
      return errorToast('Ордер уже сохраняется!');
    }

    const accruals = [
      ...rent.fine.map((elem) => ({
        accrualID1C: elem?.id,
        sum: elem.sum,
        type: 'Штраф',
        comment: elem.comment,
        accrualSubTypeID: elem.accrualSubType.id,
      })),
      ...rentMain.fine.map((elem) => ({
        accrualID1C: elem?.id,
        sum: elem.sum,
        type: 'Штраф',
        comment: elem.comment,
        accrualSubTypeID: elem.accrualSubType.id,
      })),
      ...rent.discount.map((elem) => ({
        accrualID1C: elem?.id,
        sum: elem.sum,
        type: 'Скидка',
        comment: elem.comment,
        accrualSubTypeID: elem.accrualSubType.id,
      })),
      ...rentMain.discount.map((elem) => ({
        accrualID1C: elem?.id,
        sum: elem.sum,
        type: 'Скидка',
        comment: elem.comment,
        accrualSubTypeID: elem.accrualSubType.id,
      })),
    ];

    const warrantsData = cashWarrants.map((warrant) => ({
      sum: warrant.sum,
      warrantID1C: warrant.warrantID1C,
      type: warrant.type,
      comment: warrant.comment,
      cashboxID1C: warrant.selected.cashboxID1C,
      actID: rentId || null,
      isPledge,
      isGettingBy1C: warrant.isGettingBy1C || false,
      conduct: warrant.conduct || false,
      nonConduct: warrant.nonConduct || false,
      pdfBill: warrant.pdfBill || '',
      isReadyFor1C: warrant.isReadyFor1C || false,
    }));

    setPending(true);

    const maxRentItem = rent.instruments.reduce((acc, cur) => {
      return acc?.AdditionalRentTerm < cur.AdditionalRentTerm + cur.oldAdditionalRentTerm ? cur : acc;
    }, rent.instruments[0]);
    const rentData = {
      agreementID: rent.agr.id,
      bonusSpent: rent.bonusSpent || 0,
      bonusAccured: 0,
      accruals,
      AdditionalRentTerm: maxRentItem.AdditionalRentTerm + maxRentItem.oldAdditionalRentTerm,
      endDateTime: rent.endDateTime + maxRentItem.AdditionalRentTerm * 3600 * 24,
      comment: rent.comment,
      chosenItems: rent.instruments.map((item) => ({
        ...item,
        AdditionalRentTerm: item.AdditionalRentTerm + item.oldAdditionalRentTerm,
      })),
      is_trigger_1c: true,
    };

    const warrantsAPIData = await dispatch(CashWarrant.updateTransferActContinueBoxes(rentId, rentData, warrantsData));
    setCashWarrants(CashWarrantService.mapWarrants(warrantsAPIData));
    dispatch(RentContinue.clearRent());
    setPending(false);
  };

  const removeCashWarrant = (id) => {
    const findIndex = cashWarrants.filter((elem) => elem.createdNow).findIndex((item) => item.id === id);
    if (findIndex !== -1) {
      const newWarrants = cashWarrants.filter((elem) => elem.createdNow).slice();
      const oldSum = newWarrants[findIndex].sum >= 0 ? newWarrants[findIndex].sum : 0;

      const cashWarrantsSum = cashWarrants.filter((elem) => elem.createdNow).reduce((acc, cur) => acc + cur.sum, 0);

      /// При удалении ордера нужно передавать сумму в соседний ордер
      /// Передается в предыдущий ордер, если это первый ордер, во второй
      if (rentSum >= cashWarrantsSum) {
        if (findIndex === 0) {
          newWarrants[findIndex].sum += oldSum;
        } else {
          newWarrants[findIndex - 1].sum += oldSum;
        }
      }
      newWarrants.splice(findIndex, 1);
      setCashWarrants([...cashWarrants.filter((elem) => !elem.createdNow), ...newWarrants]);
    }
  };

  const updateCashWarrant = (id, key, value) => {
    const findIndex = cashWarrants.findIndex((item) => item.id === id);

    /// Меняем значение ордера по ключу
    if (findIndex !== -1) {
      const newWarrants = cashWarrants.slice();
      newWarrants[findIndex][key] = value;
      setCashWarrants(newWarrants);
    }
  };

  const createCashWarrant = (defalutWarrants) => {
    const warrants = defalutWarrants || cashWarrants.slice();
    const worker = userData?.cashbox || null;

    const findCashBox = worker && cashBoxes.find((item) => item.cashboxID1C === worker.cashboxID1C);
    const isUnofficialWarrant = warrants.some((w) => w.nonConduct);

    /// В новый ордер нужно добавлять остаток суммы аренды с учётом предыдущих ордеров
    const sum = rentSum - warrants.filter((elem) => elem.createdNow).reduce((acc, cur) => acc + cur.sum, 0);
    const newCashWarrant = {
      id: Date.now(),
      type: 'income',
      warrantID1C: getUUID(),
      sum: isUnofficialWarrant ? 0 : sum >= 0 ? sum : 0,
      selected: findCashBox || null,
      comment: '',
      createdNow: true,
    };

    setCashWarrants([...warrants, newCashWarrant]);
    setEditingCashWarrantId(newCashWarrant.id);
  };

  const editCashWarrant = (id) => {
    setEditingCashWarrantId(id);
  };

  const saveCashWarrant = () => {
    setEditingCashWarrantId(undefined);
  };

  const getIsUnofficialWarrant = (id) => {
    const warrant = cashWarrants.find((item) => item.id === id);
    return warrant?.nonConduct;
  };

  return (
    <>
      <Header title="Кассовый ордер" onClose={handleClose} />

      <main className="warrants-exit-list">
        <div className="warrants-exit-section">
          <div className="warrants-exit-section-list">
            <h3>Приходные кассовые ордера</h3>
            {cashWarrants
              .filter((elem) => elem.type === 'income')
              .map((item, i) => {
                if (editingCashWarrantId === item.id) {
                  return (
                    <CashWarrantForm
                      key={i}
                      cashWarrant={item}
                      number={i + 1}
                      updateCashWarrant={updateCashWarrant}
                      removeCashWarrant={removeCashWarrant}
                      cashBoxes={cashBoxes}
                      isOnlyOneCashWarrant={cashWarrants.length === 1}
                      saveCashWarrant={() => saveCashWarrant()}
                      isUnofficialWarrant={getIsUnofficialWarrant(item.id)}
                    />
                  );
                }
                return (
                  <CashWarrantCard
                    key={i}
                    cashWarrant={item}
                    rent={rent}
                    editCashWarrant={() => editCashWarrant(item.id)}
                    deleteCashWarrant={() => removeCashWarrant(item.id)}
                    isUnofficialWarrant={getIsUnofficialWarrant(item.id)}
                    onUrlClick={() => {
                      handleUrlClick(item);
                    }}
                  />
                );
              })}
            <div className="cash-warrant-group">
              <ButtonIcon className="btn-main" onClick={() => createCashWarrant()} icon="plus-round" text="Добавить" />
            </div>
          </div>
          <div className="warrants-exit-section-list">
            <h3> Расходные кассовые ордера</h3>

            {cashWarrants
              .filter((elem) => elem.type === 'cost')
              .map((item, i) => {
                if (editingCashWarrantId === item.id) {
                  return (
                    <CashWarrantForm
                      key={i}
                      cashWarrant={item}
                      number={i + 1}
                      updateCashWarrant={updateCashWarrant}
                      removeCashWarrant={removeCashWarrant}
                      cashBoxes={cashBoxes}
                      isOnlyOneCashWarrant={cashWarrants.length === 1}
                      saveCashWarrant={() => saveCashWarrant()}
                      isUnofficialWarrant={getIsUnofficialWarrant(item.id)}
                    />
                  );
                }
                return (
                  <CashWarrantCard
                    key={i}
                    cashWarrant={item}
                    rent={rent}
                    editCashWarrant={() => editCashWarrant(item.id)}
                    deleteCashWarrant={() => removeCashWarrant(item.id)}
                    isUnofficialWarrant={getIsUnofficialWarrant(item.id)}
                    onUrlClick={() => {
                      handleUrlClick(item);
                    }}
                  />
                );
              })}

            <div className="cash-warrant-group"></div>
          </div>
        </div>
        <div className="buttons-block-grid">
          <div style={{ display: 'flex' }}>
            <ButtonIcon
              className="btn-main"
              onClick={handleBack}
              icon="arrow-left"
              text="Вернуться к продлению аренды"
            />
          </div>

          <div className="flex-center">
            <ButtonIcon className="btn-main" onClick={handleSave} icon="save" text="Продлить" />
          </div>

          <div></div>
        </div>
      </main>

      {attentionModal && (
        <ModalAttention onClose={() => toggleAttentionModal(false)} onSubmit={() => handleClose(true)} />
      )}
    </>
  );
};

export { CheckoutRentContinuePage };
